import { CoreEvent, eventNames } from '@carvana/core-events';
import { useEffect } from 'react';
import { mfeModals } from '~/constants/common/MfeModules';
import useOpenMfeModal from '~/hooks/common/mfe/useOpenMfeModal';

type SoftpullModalOpenPayload = {
  isAddCoSigner: boolean;
  isEditCoSigner: boolean;
  landingHeading?: string;
  landingSubheading?: string;
  showLogo?: boolean;
};

type OwnProps = {
  canMount: boolean;
  consumer?: string;
  intendedModal?: string;
  isAddCoApplicantModalOpen: boolean;
  isEditCoApplicantModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  landingHeading?: string;
  landingSubheading?: string;
  showLogo?: boolean;
  setAddCoApplicantModalOpen: (isOpen: boolean) => void;
  setEditCoApplicantModalOpen: (isOpen: boolean) => void;
  setSoftPullModalOpen: (isOpen: boolean) => void;
};

// Translates the host's open-state flags into useOpenMfeModal requests for the Softpull MFE, which
// holds them until the MFE is ready and shows the placeholder in the meantime.
export default function useSoftPullModalSync({
  canMount,
  consumer,
  intendedModal,
  isAddCoApplicantModalOpen,
  isEditCoApplicantModalOpen,
  isSoftPullModalOpen,
  landingHeading,
  landingSubheading,
  showLogo,
  setAddCoApplicantModalOpen,
  setEditCoApplicantModalOpen,
  setSoftPullModalOpen
}: OwnProps) {
  const closeAll = () => {
    setSoftPullModalOpen(false);
    setAddCoApplicantModalOpen(false);
    setEditCoApplicantModalOpen(false);
  };
  // Also gated on canMount — see useMfeMountGate — to avoid reading a stale
  // pre-teardown value during a same-commit SPA swap.
  const { cancel, isReady, open } = useOpenMfeModal(mfeModals.softpull, {
    canOpen: canMount,
    datadogContext: {
      canMount: String(canMount),
      ...(consumer && intendedModal ? { consumer, intendedModal } : {}),
      isAddCoApplicantModalOpen: String(isAddCoApplicantModalOpen),
      isEditCoApplicantModalOpen: String(isEditCoApplicantModalOpen),
      isSoftPullModalOpen: String(isSoftPullModalOpen)
    },
    onDismiss: closeAll,
    onTimeout: closeAll,
    showPlaceholder: false
  });
  const isOpenRequested =
    isSoftPullModalOpen ||
    isAddCoApplicantModalOpen ||
    isEditCoApplicantModalOpen;

  useEffect(() => {
    if (isOpenRequested) {
      const payload: SoftpullModalOpenPayload = {
        isAddCoSigner: isAddCoApplicantModalOpen,
        isEditCoSigner: isEditCoApplicantModalOpen,
        landingHeading,
        landingSubheading,
        showLogo
      };

      open(payload as any);

      return;
    }

    cancel();

    if (isReady) CoreEvent.emit(eventNames.softpullModal.close, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isReady only decides whether to send close; it must not re-run this effect when the MFE becomes ready, since useOpenMfeModal already emits the held open request then
  }, [
    cancel,
    isAddCoApplicantModalOpen,
    isEditCoApplicantModalOpen,
    isOpenRequested,
    isSoftPullModalOpen,
    landingHeading,
    landingSubheading,
    open,
    showLogo
  ]);

  useEffect(() => {
    if (!isReady || !isOpenRequested) return undefined;

    CoreEvent.once(eventNames.softpullModal.close, closeAll);

    return () => CoreEvent.off(eventNames.softpullModal.close, closeAll);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- closeAll only calls the stable setters
  }, [isOpenRequested, isReady]);
}
